import React, { useState, useCallback } from "react";
import "./App.css";
// Both must come after App.css: they re-point the same tokens, so they have to
// win the cascade. The handoff puts these in index.js, but App.css is imported
// here, and load order is what matters. Overrides only — App.css is untouched.
import "./pokepricer-theme.css";
import "./stack-pricer.css";
import "./centering.css";
import "./binder.css";
import "./backup.css";
import "./submissions.css";
import logo from "./logo.svg";
import opLogo from "./logo-onepiece.svg";

import { SearchAdd } from "./features/search";
import { Collection } from "./features/collection";
import { NavDrawer } from "./features/nav";
import { AccountControls, useAccount } from "./shared/auth";
import useSync from "./shared/useSync";
import SyncConflict from "./features/sync/SyncConflict";
import SyncStatus from "./features/sync/SyncStatus";
import { WantList } from "./features/wantlist";
import { ListPicker } from "./features/lists";
import StackPricer from "./features/stack/StackPricer";
import { SingleCard } from "./features/single";
import { OnePieceSearch, useOnePiecePrices } from "./features/onepiece";
import { Goals } from "./features/goals";
import Centering from "./features/centering/Centering";
import Binder from "./features/binder/Binder";
import BackupPanel from "./features/backup/BackupPanel";
import Submissions from "./features/submissions/Submissions";

import { useCardSearch, useCollection, useLists, useGame, useGoals, GAMES, fetchCards, normalizeFromPPT, cardsFromPayload } from "./shared";
import useOffline, { useInstallPrompt } from "./shared/useOffline";

import { Spinner, GameToggle, ScreenBoundary, useUndo, UndoBar } from "./ui";

// Nothing ships pre-filled. These lists used to arrive holding one person's
// own want list — fine on a laptop, but the moment this was deployed every
// visitor landed on someone else's cards. Lists start empty; Import is how you
// bring your own in, including onto your phone.
const LIST_SEEDS = {};

/* Deployed, the landing page is the site root and the app lives at /app. The
   CRA dev server has no such split — it serves the app at the root, with
   public/ beside it — so the same href there would just reload the app. */
const LANDING_HREF = process.env.NODE_ENV === "production" ? "/" : "/landing.html";

// The collection sits among the lists as a chip. Deliberately not a valid list
// id, so selecting it can never be confused for selecting a list.
const COLLECTION_TAB = "__collection__";
/* Goals is a chip on the Collection screen rather than a tab in the nav. The
   nav said "what you own, and what you're after" on one entry and "what
   you're chasing" on the next, which is one sentence said twice; and Goals
   owns no cards of its own — it reads the lists and the collection total,
   both of which live on this screen. A summary belongs beside the thing it
   summarises. */
const GOALS_TAB = "__goals__";

/* The dev drawer's phrase, stored as a hash so the word itself is not sitting
   in the bundle for anyone running a text search over it. Obfuscation, not
   security — see the note by the keyboard handler. */
const djb2 = (s) => {
  let h = 5381;
  for (let i = 0; i < String(s).length; i++) h = (((h << 5) + h) + String(s).charCodeAt(i)) >>> 0;
  return h;
};
const DEV_PHRASE_HASH = 254687193;
const DEV_UNLOCK_KEY = "ppt_dev_unlocked_v1";

export default function PokePricerApp() {
  const search = useCardSearch();
  const col = useCollection();
  // Lists are created by whoever is using the app rather than hardcoded as nav
  // entries. The two that ship with content seed themselves on first run.
  const lists = useLists(LIST_SEEDS);
  /* One Piece was its own section, which quietly made every other screen
     Pokemon-only. The screens are the same shape for both games, so the game
     is a mode now — one set of screens, one switch in the top bar. */
  // hasConditions is no longer read here: the collection holds both games at
  // once, so whether a card has conditions is a question about the card.
  const { game, setGame, isOnePiece } = useGame();
  const goals = useGoals(game);

  /* A list's progress has to count "got" the same way the list itself does,
     or the goal and the list would disagree about the same cards. Owning is
     recorded in the collection where a card can enter it; the rest keep their
     own tick. */
  const listProgress = (listId) => {
    const list = lists.lists.find((l) => l.id === listId);
    if (!list) return null;
    const owned = list.game !== GAMES.ONE_PIECE;
    const had = list.cards.filter((c) =>
      owned && /^\d+$/.test(String(c.id)) ? isInCollection(c.id) : !!c.have
    ).length;
    return { done: had, total: list.cards.length };
  };

  const online = useOffline();
  const { canInstall, install, dismiss } = useInstallPrompt();

  const [navOpen, setNavOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  // Pricing a stack is the app's main job now, so it's where you land.
  const [section, setSection] = useState("stack");

  /* Single card is off for now — not deleted, unreached. The screen, its tests
     and the search links that opened it are all still here; nothing passes
     onInspect, so the links do not render, and the section is already hidden
     from the nav. Turning it back on is passing `inspect` to the two searches
     again and unhiding the nav entry.

     It works. The question it was still open on is whether a card's details
     want a whole screen at all, or an overlay over the results you were
     looking at — and that is worth answering before more is built on it. */
  const goTo = useCallback((next) => setSection(next), []);

  /* One Goals screen, reached two ways: the chip on Collection, and the old
     section route. Every list, both games — how you collect is one set of
     decisions, "singles over slabs" is not a Pokemon opinion, so splitting
     these by game would mean switching games to read your own rules. */
  const renderGoals = () => (
    <Goals
      goals={goals.goals}
      addGoal={goals.addGoal}
      removeGoal={goals.removeGoal}
      toggleDone={goals.toggleDone}
      lists={allLists}
      listProgress={listProgress}
      collectionValue={col.totalValue}
    />
  );

  /* Deleting, with a way back.
   *
   * One offer for the whole Collection screen, whichever chip is showing —
   * a card taken off a list and a card taken out of the collection are the
   * same mistake, and the same "wait, no". Each removal hands back exactly
   * what it would take to reverse it, and the offer holds that for a few
   * seconds. Only the newest: a second delete commits the first. */
  const undo = useUndo();

  const removeFromListWithUndo = useCallback((listId, cardId) => {
    const taken = lists.removeCard(listId, cardId);
    if (taken) undo.offer(taken.card.name, () => lists.restoreCard(listId, taken.card, taken.index));
  }, [lists, undo]);

  const removeFromCollectionWithUndo = useCallback((cardId) => {
    const taken = col.removeFromCollection(cardId);
    if (taken) undo.offer(taken.card.name, () => col.restoreToCollection(taken.card, taken.index));
  }, [col, undo]);

  const [movingId, setMovingId] = useState(null);
  const [moveError, setMoveError] = useState("");
  // Which chip is showing under "My cards": the collection, or one of the
  // lists. Not a list id, so it can never collide with one.
  const [showingCollection, setShowingCollection] = useState(true);
  const [showingGoals, setShowingGoals] = useState(false);
  const [importNote, setImportNote] = useState("");

  // The want list stores no prices on purpose, so moving a card into the
  // collection fetches current ones rather than carrying stale numbers across.
  // The card stays on the list — you still wanted it — and reads as got
  // because the collection now holds it. Nothing is written back to the list.
  const moveToCollection = async (card) => {
    setMovingId(card.id);
    setMoveError("");
    try {
      const data = await fetchCards({ tcgPlayerId: card.id });
      const fresh = cardsFromPayload(data).map(normalizeFromPPT).filter(Boolean)[0];
      if (!fresh) throw new Error("That card came back without pricing.");
      col.addToCollection(fresh);
    } catch (e) {
      const status = e?.response?.status;
      setMoveError(
        status === 429
          ? "Rate limit hit. Wait a moment and try again."
          : `Couldn't add ${card.name}: ${e?.response?.data?.message || e.message}`
      );
    } finally {
      setMovingId(null);
    }
  };

  const isInCollection = (id) => col.collection.some((c) => c.id === id);

  // Cards used to land on whichever list was "active", which is just the one
  // you last made or clicked. The buttons now offer the lists by name, so
  // these two say which lists exist and which one already holds a card.
  const gameLists = lists.listsFor(game);
  const listNameFor = (cardId) =>
    gameLists.find((l) => l.cards.some((c) => c.id === cardId))?.name;

  /* The Collection section shows every list, both games, beside one collection
     holding everything — your cards are your cards. Which game a card belongs
     to still decides what can be done with it, so that is read off the card
     and the list rather than off the toggle, which is not shown there. */
  const allLists = lists.lists;
  const [openListId, setOpenListId] = useState(null);
  const openList = allLists.find((l) => l.id === openListId) || null;
  const openListIsOp = openList?.game === GAMES.ONE_PIECE;

  // Only a One Piece list needs prices fetching; a Pokemon list stores none.
  const opPrices = useOnePiecePrices(
    openListIsOp && !showingCollection ? openList?.cards : null
  );

  // Both list sections render the same way; only the game and what a card can
  // do with a price differ. The Pokemon one also carries the collection as a
  // pinned first chip — see COLLECTION_TAB.
  const renderLists = (game, opts = {}) => {
    const onFixed = opts.pinned && (showingCollection || showingGoals);
    const active = onFixed ? null : (opts.pinned ? openList : lists.activeFor(game));
    const activeId = !opts.pinned
      ? active?.id ?? null
      : showingGoals ? GOALS_TAB
      : showingCollection ? COLLECTION_TAB
      : active?.id ?? null;
    return (
      <>
        <ListPicker
          lists={opts.pinned ? allLists : lists.listsFor(game)}
          activeId={activeId}
          pinned={opts.pinned}
          fixed={opts.fixed}
          onSelect={(id) => {
            if (id === COLLECTION_TAB) { setShowingGoals(false); return setShowingCollection(true); }
            if (id === GOALS_TAB) { setShowingCollection(false); return setShowingGoals(true); }
            setShowingCollection(false);
            setShowingGoals(false);
            if (opts.pinned) setOpenListId(id);
            else lists.setActive(lists.lists.find((l) => l.id === id)?.game || game, id);
          }}
          onCreate={(name) => {
            setShowingCollection(false);
            setShowingGoals(false);
            const id = lists.createList(name, game);
            if (opts.pinned) setOpenListId(id);
            return id;
          }}
          onRename={lists.renameList}
          onDelete={lists.deleteList}
          onImport={(name, cards, { intoPinned } = {}) => {
            if (intoPinned) {
              const added = col.importCards(cards);
              setImportNote(
                added
                  ? `Added ${added} card${added === 1 ? "" : "s"} to your collection.`
                  : "Every card in that file was already in your collection."
              );
              return;
            }
            setShowingCollection(false);
            setShowingGoals(false);
            const id = lists.importList(name, game, cards);
            if (opts.pinned) setOpenListId(id);
          }}
          game={game}
        />
        {opts.pinned && showingGoals ? (
          opts.renderGoals()
        ) : opts.pinned && showingCollection ? (
          <>
            {importNote && <p className="msg-empty">{importNote}</p>}
            {opts.renderCollection()}
          </>
        ) : active ? (
          <WantList
            title={active.name}
            wantList={active.cards}
            removeFromWantList={(cardId) => removeFromListWithUndo(active.id, cardId)}
            cycleStatus={(cardId) => lists.cycleStatus(active.id, cardId)}
            toggleHave={(cardId) => lists.toggleHave(active.id, cardId)}
            onGoToSearch={opts.onGoToSearch}
            /* Read off the list, not off the toggle: moving a card into the
               collection refetches its price, which only the Pokemon path can
               do, and only a One Piece list has prices to show. */
            onMoveToCollection={active.game === GAMES.ONE_PIECE ? undefined : opts.onMoveToCollection}
            onRemoveFromCollection={active.game === GAMES.ONE_PIECE ? undefined : opts.onRemoveFromCollection}
            isInCollection={active.game === GAMES.ONE_PIECE ? undefined : opts.isInCollection}
            movingId={opts.movingId}
            moveError={opts.moveError}
            prices={active.game === GAMES.ONE_PIECE ? opPrices.prices : undefined}
            pricesState={active.game === GAMES.ONE_PIECE ? opPrices.state : undefined}
          />
        ) : (
          <div className="section-empty">
            <h2 className="section-title">No lists yet</h2>
            <p>{opts.emptyHint}</p>
          </div>
        )}
      </>
    );
  };

  const [showDebug, setShowDebug] = useState(false);
  const [showCollectionDebug, setShowCollectionDebug] = useState(false);
  const [copiedDebug, setCopiedDebug] = useState(false);
  const [copiedCollectionDebug, setCopiedCollectionDebug] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [devToolsVisible, setDevToolsVisible] = useState(false);
  const [askingPass, setAskingPass] = useState(false);
  const [passDraft, setPassDraft] = useState("");
  const [passWrong, setPassWrong] = useState(false);
  // Unlocking lasts the browser session, so the phrase is not retyped every
  // time the panel is toggled, but a fresh tab starts locked again.
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return window.sessionStorage.getItem(DEV_UNLOCK_KEY) === "1";
    } catch {
      return false;
    }
  });

  /* Ctrl+Shift+D asks for a phrase before revealing the drawer.
     This is a latch, not a lock. Anything the front-end knows is in the
     bundle and readable by anyone who opens it, so the check below stops idle
     curiosity and nothing more. That is the right strength here: the panel
     shows the visitor their own API responses, and the keys that would
     actually matter live on the Worker where the browser never sees them. */
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        if (devToolsVisible) return setDevToolsVisible(false);
        if (unlocked) { setDebugOpen(false); return setDevToolsVisible(true); }
        setPassDraft("");
        setPassWrong(false);
        setAskingPass((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [devToolsVisible, unlocked]);

  const submitPass = () => {
    if (djb2(passDraft) !== DEV_PHRASE_HASH) {
      setPassWrong(true);
      setPassDraft("");
      return;
    }
    try {
      window.sessionStorage.setItem(DEV_UNLOCK_KEY, "1");
    } catch {
      /* a blocked sessionStorage just means retyping it next time */
    }
    setUnlocked(true);
    setAskingPass(false);
    setDevToolsVisible(true);
    setDebugOpen(false);
  };

  const copyText = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopyDebug = async () => {
    const payload = search.debugInfo ? JSON.stringify(search.debugInfo, null, 2) : "No debug info available";
    if (await copyText(payload)) {
      setCopiedDebug(true);
      setTimeout(() => setCopiedDebug(false), 1200);
    }
  };

  const handleCopyCollectionDebug = async () => {
    const payload = JSON.stringify(col.collectionDebug, null, 2);
    if (await copyText(payload)) {
      setCopiedCollectionDebug(true);
      setTimeout(() => setCopiedCollectionDebug(false), 1200);
    }
  };

  /* Does nothing at all signed out, which is the point — the app has always
     worked with everything in one browser and still does. */
  const sync = useSync();
  const { signedIn } = useAccount();

  return (
    <div className="app-container">
      <SyncConflict
        conflict={sync.conflict}
        busy={sync.state === "syncing"}
        onChoose={sync.resolve}
        onClose={sync.dismiss}
      />
      <SyncStatus
        state={sync.state}
        paused={sync.paused}
        onReopen={sync.reopen}
        onRetry={sync.syncNow}
      />
      <header className="topbar">
        <button
          className="hamburger"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? "Close menu" : "Open menu"}
          aria-expanded={navOpen}
          aria-controls="nav-drawer"
        >
          <span className="hamburger-bars" aria-hidden="true" />
        </button>
        {/* The one strip on every screen, so it carries the mark. A real link
            rather than a button: it leaves the app, so it should behave like a
            link — middle-click, open in a new tab, and a visible target. */}
        <a
          className="topbar-brand"
          href={LANDING_HREF}
          title="PokePricer home"
        >
          {/* The mark follows the game, so the top bar says which one you're
              in even on the screens that have no toggle. */}
          <img src={isOnePiece ? opLogo : logo} alt="" className="topbar-logo" />
          <span className="topbar-title">Poke<em>Pricer</em></span>
        </a>
        {/* Only where it has a job. Collection and Goals hold both games at
            once, so a switch there would change nothing — and a control that
            does nothing is worse than no control. */}
        {section !== "cards" && section !== "goals" && section !== "grading"
          && section !== "binder" && section !== "submissions" && (
          <GameToggle game={game} onChange={setGame} />
        )}
        {/* Last in the bar, and absent entirely until accounts are switched
            on. Nothing here changes where data is kept — signing in says who
            you are and stops there. */}
        <AccountControls />
      </header>

      {/* Said out loud rather than left to be discovered when a price does not
          arrive. Everything the app keeps is on the device, so most of it
          carries on working — which is worth saying too. */}
      {!online && (
        <p className="offline-bar">
          No connection. Your collection, lists, goals and binder all still work —
          prices and card searches will not until you are back.
        </p>
      )}

      {canInstall && (
        <p className="install-bar">
          Put PokePricer on your home screen and it opens like an app, and works at
          a show with no signal.
          <button type="button" onClick={install}>Add it</button>
          {/* Saying no once means no. Without this the bar comes back on every
              visit until the browser gives up on its own, which is the whole
              reason install prompts are resented. */}
          <button
            type="button"
            className="install-no"
            onClick={dismiss}
            aria-label="Don't show this again"
            title="Don't show this again"
          >
            ×
          </button>
        </p>
      )}

      <NavDrawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        active={section}
        onNavigate={goTo}
        onBackup={() => setBackupOpen(true)}
        devUnlocked={unlocked}
        signedIn={signedIn}
      />

      {backupOpen && <BackupPanel onClose={() => setBackupOpen(false)} />}

      {/* Two searches behind one screen. They are genuinely different: Pokemon
          queries a metered API and needs set filters and paging, One Piece
          reads a catalogue bundled into the app and can show every print of a
          card side by side. Forcing them into one component would serve
          neither. */}
      {section === "search" && isOnePiece && (
        <>
          <h2 className="section-title">Search</h2>
          <OnePieceSearch
            alwaysOpen
            targetName={lists.activeFor(GAMES.ONE_PIECE)?.name}
            isOnList={(id) => lists.isOnAnyList(id, GAMES.ONE_PIECE)}
            // Makes the One Piece list on the way in, rather than needing one
            // to have existed before the app was ever useful.
            onAdd={(card) => lists.addCardForGame(GAMES.ONE_PIECE, card)}
            onAddToCollection={(card) => col.addToCollection(card, GAMES.ONE_PIECE)}
            isInCollection={isInCollection}
          />
        </>
      )}

      {section === "search" && !isOnePiece && (
      <div className="hero">
        {/* The big masthead used to live here, which made Search the only
            screen with centred branding. The top bar carries the mark on
            every screen now, so this screen gets a heading like the rest. */}
        <h2 className="section-title">Search</h2>

        <SearchAdd
          cardName={search.cardName}
          setCardName={search.setCardName}
          cardNumber={search.cardNumber}
          setCardNumber={search.setCardNumber}
          showBulk={search.showBulk}
          setShowBulk={search.setShowBulk}
          bulkInput={search.bulkInput}
          setBulkInput={search.setBulkInput}
          onSearch={search.handleSearch}
          onBulkAdd={() => search.handleBulkAdd(col.addToCollection)}
          searchResults={search.searchResults}
          addToCollection={col.addToCollection}
          hasMore={search.hasMore}
          onSeeMore={search.handleSeeMore}
          loading={search.loading}
          availableSets={search.availableSets}
          selectedSets={search.setFilters}
          onSelectSets={search.setSetFilters}
          psaPriceCache={search.psaPriceCache}
          onLoadPsaPricing={search.loadPsaPricing}
          addToWantList={(card, listId) => lists.addCard(listId, card)}
          isOnWantList={(id) => lists.isOnAnyList(id, GAMES.POKEMON)}
          isInCollection={isInCollection}
          wantLists={gameLists}
          listNameFor={listNameFor}
          hideOversize={search.hideOversize}
          onHideOversize={search.setHideOversize}
          oversizeHiddenCount={search.oversizeHiddenCount}
        />
      </div>
      )}

      {section === "search" && !isOnePiece && (
        <>
          {search.loading && <Spinner />}
          {!search.loading && !search.error && Array.isArray(search.setFilters) && search.setFilters.length > 0 && search.searchResults.length === 0 && search.rawSearchResults.length > 0 && (
            <p className="msg-empty">No cards from the selected set were found. Try another set or refine your search.</p>
          )}
          {search.error && <p className="msg-error">{search.error}</p>}
          {search.bulkErrors.length > 0 && (
            <div className="msg-error">
              {search.bulkErrors.length} line{search.bulkErrors.length === 1 ? "" : "s"} didn't add:
              <ul className="bulk-error-list">
                {search.bulkErrors.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Goals still routes as a section — hidden from the nav, the same flag
          Grading and Binder use — so nothing that links to it breaks. It
          renders the same screen the Goals chip does. */}
      {section === "goals" && renderGoals()}

      {/* Keyed on the game so switching starts fresh. Without it these stay
          mounted and only the prop changes, which left a priced Pokemon stack
          sitting on screen while the app said One Piece — prices belonging to
          a game you are no longer looking at.

          The stack is the one screen that keeps its own durable state, so it
          is the one that can be brought down by it. The offer inside is to
          forget that key and nothing else — never somebody's collection. */}
      {section === "stack" && (
        <ScreenBoundary
          key={game}
          title="The stack"
          what="the saved stack"
          storageKey="ppt_stack_v1"
        >
          <StackPricer isOnePiece={isOnePiece} devUnlocked={unlocked} />
        </ScreenBoundary>
      )}

      {/* No game toggle above: a card is a rectangle with a border whichever
          game printed it, and the measurement knows nothing about either. */}
      {section === "grading" && <Centering />}

      {/* No game toggle: a binder page is a grid of card-shaped holes, and the
          cards that go in it come from whichever search you used. */}
      {section === "binder" && <Binder />}

      {/* No game toggle: a submission is money in and money out, and the
          grader does not care which game printed the card. */}
      {section === "submissions" && <Submissions game={game} />}

      {section === "single" && (
        <SingleCard
          key={game}
          isOnePiece={isOnePiece}
          addToCollection={(card) => col.addToCollection(card, game)}
          addToWantList={(card, listId) => lists.addCard(listId, card)}
          isOnWantList={(id) => lists.isOnAnyList(id, game)}
          isInCollection={isInCollection}
          wantLists={gameLists}
          listNameFor={listNameFor}
        />
      )}

      {section === "cards" && <UndoBar pending={undo.pending} onUndo={undo.undo} />}
      {section === "cards" && renderLists(game, {
        onGoToSearch: () => setSection("search"),
        // Whether these apply is decided per list, inside renderLists.
        onMoveToCollection: moveToCollection,
        onRemoveFromCollection: removeFromCollectionWithUndo,
        isInCollection,
        movingId,
        moveError,
        emptyHint: "Make one above, then add cards to it from Search.",
        // One collection holding both games. Your cards are your cards.
        pinned: {
          id: COLLECTION_TAB,
          name: "Collection",
          game,
          cards: col.collection,
        },
        fixed: [
          {
            id: GOALS_TAB,
            name: "Goals",
            count: goals.goals.length || null,
            title: "What you're chasing, and what you're not",
          },
        ],
        renderGoals,
        renderCollection: () =>
          col.collection.length === 0 ? (
            <div className="section-empty">
              <h2 className="section-title">Collection</h2>
              <p>Nothing added yet. Search for a card and add it — or Import a collection you exported before.</p>
              <button className="btn" onClick={() => setSection("search")}>Go to Search</button>
            </div>
          ) : (
            <Collection
              collection={col.collection}
              updateQty={col.updateQty}
              updateCondition={col.updateCondition}
              removeFromCollection={removeFromCollectionWithUndo}
              toggleSlab={col.toggleSlab}
              updatePsaGrade={col.updatePsaGrade}
              getCardPrice={col.getCardPrice}
              setOwnPrice={col.setOwnPrice}
              totalValue={col.totalValue}
              unpricedCount={col.unpricedCount}
              percentage={col.percentage}
              setPercentage={col.setPercentage}
            />
          ),
      })}

      {/* Asked for with Ctrl+Shift+D, then a phrase. It's a developer panel
          that prints raw API responses; on a shop floor it's just clutter. */}
      {askingPass && !devToolsVisible && (
        <div className="dev-gate">
          <label className="stack-label" htmlFor="dev-phrase">DEV TOOLS</label>
          <input
            autoFocus
            id="dev-phrase"
            className="dev-gate-input"
            type="password"
            value={passDraft}
            placeholder="Passphrase"
            aria-label="Dev tools passphrase"
            onChange={(e) => { setPassDraft(e.target.value); setPassWrong(false); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitPass();
              if (e.key === "Escape") setAskingPass(false);
            }}
          />
          <button className="btn btn-primary btn-sm" onClick={submitPass}>Unlock</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setAskingPass(false)}>Cancel</button>
          {passWrong && <span className="dev-gate-no">Not that</span>}
        </div>
      )}

      {devToolsVisible && (
      <div className="debug-drawer">
        <button className="debug-drawer-toggle" onClick={() => setDebugOpen((v) => !v)}>
          {debugOpen ? "Hide Dev Tools" : "Dev Tools"}
        </button>
        {debugOpen && (
          <div className="debug-drawer-content">
            <div className="debug-drawer-row">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowDebug((v) => !v)}>
                {showDebug ? "Hide Search Debug" : "Search Debug"}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleCopyDebug}>Copy</button>
              {copiedDebug && <span className="msg-copied">Copied!</span>}
            </div>
            <div className="debug-drawer-row">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCollectionDebug((v) => !v)}>
                {showCollectionDebug ? "Hide Collection Debug" : "Collection Debug"}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleCopyCollectionDebug}>Copy</button>
              {copiedCollectionDebug && <span className="msg-copied">Copied!</span>}
            </div>
            {showDebug && <pre className="debug-pre">{JSON.stringify(search.debugInfo, null, 2)}</pre>}
            {showCollectionDebug && <pre className="debug-pre">{JSON.stringify(col.collectionDebug.slice(0, 30), null, 2)}</pre>}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
